import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import { Camera, MessageSquare, Pencil, Lock, Building2 } from 'lucide-react-native';
import { useTheme, TYPOGRAPHY, SPACING, BORDER_RADIUS } from '../../theme';
import { modelNameForItem } from '../../constants/checklistTopics';
import { formatRelativeTime } from '../../utils/time';
import type { ItemEvidence } from '../../utils/checklistEvidence';
import type { Post } from '../../types';

interface Props {
  evidence?: ItemEvidence;
  /** Viewer's uid, so their own observations get an edit affordance. */
  currentUserId?: string;
}

/** Distinct model names behind the borrowed observations, in first-seen order. */
function sourceModels(posts: Post[]): string[] {
  const names: string[] = [];
  for (const post of posts) {
    const name = modelNameForItem(post.checklistItemId);
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

function attributionLine(posts: Post[]): string | null {
  const names = sourceModels(posts);
  if (names.length === 0) return null;
  if (names.length === 1) return `Carried over from ${names[0]}`;
  return 'Carried over from earlier models';
}

function VisibilityTag({ visibility }: { visibility?: string }) {
  const { colors } = useTheme();
  if (!visibility || visibility === 'public') return null;
  const isPrivate = visibility === 'private';
  const Icon = isPrivate ? Lock : Building2;
  return (
    <View style={styles.tag}>
      <Icon size={10} color={colors.textTertiary} />
      <Text style={[styles.tagText, { color: colors.textTertiary }]}>
        {isPrivate ? 'Private' : 'Insurer only'}
      </Text>
    </View>
  );
}

function ObservationRow({
  post,
  borrowedFrom,
  canEdit,
}: {
  post: Post;
  borrowedFrom?: string;
  canEdit: boolean;
}) {
  const { colors } = useTheme();
  const when = formatRelativeTime(post.createdAt);
  const who = post.isAnonymous ? 'Anonymous' : post.authorName;
  const byline = [who, when].filter(Boolean).join(' · ');

  return (
    <View style={[styles.row, { borderTopColor: colors.divider }]}>
      <TouchableOpacity
        style={styles.rowMain}
        onPress={() => router.push(`/post/${post.id}`)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={
          borrowedFrom ? `View observation from ${borrowedFrom}` : 'View observation'
        }>
        {post.thumbnailUrl || post.imageUrl ? (
          <Image
            // Prefer the 400x400 WebP thumbnail; fall back to the original
            // while the async resize is in flight.
            source={post.thumbnailUrl || post.imageUrl}
            style={[styles.thumb, { backgroundColor: colors.surface }]}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={150}
          />
        ) : (
          // A text-only observation has no image. Rendering the Image anyway
          // left an empty grey square that read as a broken photo.
          <View style={[styles.thumb, styles.textThumb, { backgroundColor: colors.surface }]}>
            <MessageSquare size={15} color={colors.textTertiary} />
          </View>
        )}
        <View style={styles.rowText}>
          {post.caption ? (
            <Text style={[styles.caption, { color: colors.text }]} numberOfLines={2}>
              {post.caption}
            </Text>
          ) : null}
          <View style={styles.bylineRow}>
            <Text style={[styles.byline, { color: colors.textTertiary }]} numberOfLines={1}>
              {byline}
            </Text>
            <VisibilityTag visibility={post.visibility} />
          </View>
        </View>
      </TouchableOpacity>
      {canEdit && (
        <TouchableOpacity
          style={styles.edit}
          onPress={() => router.push(`/post/create?editPostId=${post.id}`)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Edit observation">
          <Pencil size={13} color={colors.textSecondary} />
        </TouchableOpacity>
      )}
    </View>
  );
}

/**
 * The observations documenting a checklist item: the ones filed against it,
 * then any filed under an equivalent item in another preparation model.
 *
 * A list rather than a strip of thumbnails, because the point is to see what is
 * actually there — caption, who filed it, when, and whether it is private —
 * and to edit it without leaving the checklist.
 */
export function ChecklistItemEvidenceList({ evidence, currentUserId }: Props) {
  const { colors } = useTheme();
  const direct = evidence?.direct ?? [];
  const related = evidence?.related ?? [];
  const total = direct.length + related.length;
  if (total === 0) return null;

  const attribution = attributionLine(related);

  return (
    <View style={[styles.container, { borderTopColor: colors.divider }]}>
      <View style={styles.header}>
        <Camera size={13} color={colors.textSecondary} />
        <Text style={[styles.headerText, { color: colors.textSecondary }]}>
          {total === 1 ? '1 observation' : `${total} observations`}
        </Text>
      </View>

      {direct.map((post) => (
        <ObservationRow key={post.id} post={post} canEdit={post.authorId === currentUserId} />
      ))}

      {related.length > 0 && (
        <>
          {attribution && (
            <Text style={[styles.attribution, { color: colors.textTertiary }]}>{attribution}</Text>
          )}
          {related.map((post) => (
            <ObservationRow
              key={post.id}
              post={post}
              borrowedFrom={modelNameForItem(post.checklistItemId)}
              canEdit={post.authorId === currentUserId}
            />
          ))}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingBottom: SPACING.xs,
  },
  headerText: {
    ...TYPOGRAPHY.label,
  },
  // Borrowed observations sit under their own line rather than behind a divider
  // rule: they are real evidence, not stale data, so nothing dims them.
  attribution: {
    ...TYPOGRAPHY.caption,
    paddingTop: SPACING.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.sm,
    gap: SPACING.sm,
  },
  rowMain: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: SPACING.sm,
  },
  rowText: {
    flex: 1,
    gap: 2,
  },
  caption: {
    ...TYPOGRAPHY.bodySmall,
  },
  bylineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  byline: {
    ...TYPOGRAPHY.caption,
    flexShrink: 1,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  tagText: {
    ...TYPOGRAPHY.caption,
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: BORDER_RADIUS.sm,
  },
  textThumb: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  edit: {
    padding: SPACING.xs,
  },
});
